/**
 * The paging and sorting state a list screen holds.
 *
 * Every table on every screen needs the same four values and the same three
 * interactions, and hand-writing them per page is how one table ends up not
 * resetting to page one when its filter changes — landing the reader on an empty
 * page they cannot explain.
 *
 * It holds **state, not results.** The screen still does the fetching with
 * `useAsync`, passing `query` in and listing it in the deps. That is the same
 * bargain the rest of `hooks/` makes: a hook here never knows an endpoint.
 *
 * ```tsx
 * const list = useListQuery({ defaultSort: 'displayName', resetKey: search });
 * const users = useAsync(() => listUsers({ q: search, ...list.query }), [search, list.key]);
 * ```
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ListQueryParams, SortOrder } from '@forsys/shared';

export interface UseListQueryOptions {
  /** The field the screen starts sorted on. Must be one the endpoint accepts. */
  defaultSort: string;
  defaultOrder?: SortOrder;
  defaultLimit?: number;
  /**
   * Anything that changes what is being listed rather than how — a search term,
   * a filter toggle, the folder being browsed.
   *
   * When it changes the offset goes back to zero. Without that, typing in a
   * search box while on page four asks for rows 75-100 of a result set that now
   * has three, and the screen goes blank with no explanation. Leaving it to each
   * page to remember is how one of them forgets.
   */
  resetKey?: unknown;
}

export interface ListQueryState {
  /** Spread into the API call. */
  query: ListQueryParams;
  /** Zero-based, as MUI's pager counts. */
  page: number;
  limit: number;
  sort: string;
  order: SortOrder;
  /**
   * One value summarising the request, for a `useAsync` dependency list.
   *
   * `query` is a new object every render, so depending on it re-requests
   * forever — the same trap `useAsync` documents for `operation`.
   */
  key: string;
  /**
   * Sorts by `field`, or flips the direction if it is already the sorted one.
   *
   * Always returns to page one: page four of a list sorted by name has nothing
   * to do with page four of the same list sorted by date, and staying put makes
   * a sort look like it lost rows.
   */
  toggleSort: (field: string) => void;
  setPage: (page: number) => void;
  /** Changing the page size returns to page one, for the same reason. */
  setLimit: (limit: number) => void;
}

export function useListQuery(options: UseListQueryOptions): ListQueryState {
  const { defaultSort, defaultOrder = 'asc', defaultLimit = 25, resetKey } = options;

  const [page, setPageState] = useState(0);
  const [limit, setLimitState] = useState(defaultLimit);

  // One piece of state, not two. `toggleSort` has to read the current field to
  // decide what the new direction is, and splitting them meant deriving one
  // inside the other's updater — a state updater with a side effect in it.
  // React may invoke an updater more than once (StrictMode does so deliberately,
  // to surface exactly this), which queued the direction flip twice and landed
  // it back where it started: clicking a sorted column did nothing at all.
  const [sorting, setSorting] = useState<{ field: string; order: SortOrder }>({
    field: defaultSort,
    order: defaultOrder,
  });

  // Compared rather than listed as a dependency: `resetKey` is frequently an
  // object or an array, and a dependency on one would reset the page on every
  // render. Serialising it compares by value, which is what "the filter
  // changed" actually means.
  const serialised = JSON.stringify(resetKey ?? null);
  const previous = useRef(serialised);

  useEffect(() => {
    if (previous.current === serialised) return;
    previous.current = serialised;
    setPageState(0);
  }, [serialised]);

  const toggleSort = useCallback((field: string) => {
    // Pure: it reads the state it is handed and returns the next one. Calling
    // another setter from in here is what broke the direction toggle.
    setSorting((current) =>
      current.field === field
        ? { field, order: current.order === 'asc' ? 'desc' : 'asc' }
        : // A different column starts ascending rather than inheriting the
          // previous column's direction, which would otherwise mean the first
          // click on a date column sorted it oldest-first for no visible reason.
          { field, order: 'asc' },
    );
    setPageState(0);
  }, []);

  const setLimit = useCallback((next: number) => {
    setLimitState(next);
    setPageState(0);
  }, []);

  return {
    query: { limit, offset: page * limit, sort: sorting.field, order: sorting.order },
    page,
    limit,
    sort: sorting.field,
    order: sorting.order,
    key: `${sorting.field}:${sorting.order}:${limit}:${page * limit}`,
    toggleSort,
    setPage: setPageState,
    setLimit,
  };
}
